/**
 * TicketEmployeeWorkbench — DIGEMP-TICKET-EMPLOYEE-WORKBENCH-FRONTEND-047.
 *
 * Unified operator surface inside the ticket context: assignment, progress, approvals,
 * evidence and the clear next action. Composes the existing authoritative endpoints
 * (agent-runs / conversations / agents) and the same permission gates the run panels use
 * (agents_view to see, agents_run to start, agents_approve to approve/reject); approve and
 * reject delegate to agentRunsApi — this component never implements its own approval flow.
 * Missing or unknown evidence is labeled as such (never shown as success) and raw run
 * payloads/telemetry are not rendered — human-readable fields only. Unrecognized run or
 * conversation statuses are labeled as unrecognized (the raw code stays as diagnostic detail,
 * never guessed into a state) and an async approval error is scoped to the ticket it happened
 * on, so nothing leaks across tickets. Conversation states use the canonical operator
 * vocabulary (EmployeeWorkspace CONVERSATION_STATE) so a state reads the same everywhere.
 */
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { agentsApi, agentRunsApi, conversationsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { REJECT_QUICK_REASONS } from "../utils/agentRunConstants";
import EmployeeAvatar from "./EmployeeAvatar";
import TicketEvidenceWorkflow from "./TicketEvidenceWorkflow";
import { degradedAnalysisLabel } from "./degradedAnalysisVocabulary";
import { actionTypeLabel } from "./actionTypeVocabulary";
import { riskGradeLabel } from "./riskGradeVocabulary";
import { conversationStatusMeta } from "./conversationStatusVocabulary";
import { workOutcomeForRunStatus, workOutcomeMeta } from "./workOutcomeVocabulary";

// ─── Vocabulary (mirrors AgentRunPanel / AgentRunsDashboardPage so statuses read the same) ───

// The chip's TONE is no longer decided here: it comes from the shared four-outcome vocabulary
// (workOutcomeVocabulary), which is also what the Digital Workforce console draws. The three sets
// below stay — they answer a different question (which runs this panel lists, and what the next
// action is), and their ACTIVE set is `employee_workspace.working_run_statuses()`.
const ACTIVE_STATUSES = new Set(["pending", "analyzing", "diagnosing", "connection_active", "executing"]);
const REVIEW_STATUSES = new Set(["awaiting_technician_review", "awaiting_approval"]);
const DONE_STATUSES = new Set(["completed", "resolved", "approved"]);

const STATUS_LABELS = {
  pending: "מאתחל...",
  analyzing: "מנתח קריאה...",
  diagnosing: "מאבחן...",
  executing: "מבצע",
  connection_active: "חיבור פעיל — מאבחן",
  awaiting_technician_review: "ממתין לסקירת טכנאי",
  awaiting_approval: "ממתין לאישור",
  waiting_for_customer_connection: "ממתין לחיבור ללקוח",
  waiting_for_customer_answer: "ממתין לתשובת לקוח",
  paused: "מושהה",
  completed: "הושלם",
  resolved: "נפתר",
  approved: "אושר",
  rejected: "נדחה",
  failed: "נכשל",
};

const RISK_LABELS = { green: "נמוך", amber: "בינוני", red: "גבוה" };

const OUTCOME_LABELS = { proposed: "מוצע", approved: "אושר", rejected: "נדחה", succeeded: "הצליח", failed: "נכשל" };
const OUTCOME_COLORS = {
  proposed: "text-gray-500",
  approved: "text-green-600",
  rejected: "text-red-600",
  succeeded: "text-green-600",
  failed: "text-red-600",
};

const NEXT_ACTION_TONES = {
  approval: "bg-amber-50 border-amber-200",
  active: "bg-violet-50 border-violet-200",
  wait: "bg-blue-50 border-blue-200",
  error: "bg-red-50 border-red-200",
  done: "bg-green-50 border-green-200",
  idle: "bg-gray-50 border-gray-200",
};

// Canonical operator vocabulary for conversation states (EmployeeWorkspace CONVERSATION_STATE,
// the same set the ticket-coworker model mirrors) — all seven stored statuses get their operator
// label; an unrecognized status is labeled as unrecognized with the raw code kept as detail.
// Keep this page's badge palette, but take conversation labels from the shared vocabulary.
const CONVERSATION_BADGE = {
  open: "bg-blue-100 text-blue-700 border-blue-200",
  awaiting_agent: "bg-violet-100 text-violet-700 border-violet-200",
  awaiting_human: "bg-orange-100 text-orange-700 border-orange-200",
  awaiting_approval: "bg-amber-100 text-amber-700 border-amber-200",
  handed_off: "bg-teal-100 text-teal-700 border-teal-200",
  resolved: "bg-green-100 text-green-700 border-green-200",
  closed: "bg-gray-200 text-gray-500 border-gray-300",
};
const CONVERSATION_STATE_UNKNOWN_BADGE = "bg-gray-100 text-gray-600 border-gray-200";

const conversationStateOf = (status) => {
  const meta = conversationStatusMeta(status);
  return {
    label: meta?.label ?? (status ? `מצב שיחה לא מוכר: ${status}` : "מצב שיחה לא ידוע"),
    badge: meta ? CONVERSATION_BADGE[status] : CONVERSATION_STATE_UNKNOWN_BADGE,
  };
};

// ─── Small helpers ───

const asUtc = (iso) => (typeof iso === "string" && !/Z|[+-]\d\d:\d\d$/.test(iso) ? `${iso}Z` : iso);
const timeValue = (...values) => {
  for (const value of values) {
    if (!value) continue;
    const parsed = new Date(asUtc(value)).getTime();
    if (!Number.isNaN(parsed)) return parsed;
  }
  return 0;
};
const fmtDateTime = (iso) => {
  const t = timeValue(iso);
  if (!t) return null;
  try {
    return new Date(t).toLocaleString("he-IL", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return null;
  }
};
const timeAgo = (iso) => {
  const t = timeValue(iso);
  if (!t) return null;
  const minutes = Math.floor((Date.now() - t) / 60000);
  if (minutes < 1) return "כעת";
  if (minutes === 1) return "לפני דקה";
  if (minutes < 60) return `לפני ${minutes} דק׳`;
  const hours = Math.floor(minutes / 60);
  if (hours === 1) return "לפני שעה";
  if (hours < 24) return `לפני ${hours} שע׳`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "לפני יום" : `לפני ${days} ימים`;
};
const truncate = (text, max = 200) => {
  const clean = String(text || "").replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).trimEnd()}…`;
};
const errorDetail = (error, fallback) => error?.response?.data?.detail || fallback;
const tokenExpired = (token) => !token.used && !token.revoked && timeValue(token.expires_at) > 0 && timeValue(token.expires_at) < Date.now();

// ─── Presentation pieces ───

// The word is the run's own status (STATUS_LABELS, read at full resolution); the tone is the
// §14C.1 work outcome it belongs to. Both are needed: the outcome is what makes two different
// statuses read consistently across surfaces, and the status word is what keeps the record itself
// on screen. `data-work-outcome` exposes the tone's identity rather than leaving it to be inferred
// from a class string, and is the same attribute `WorkOutcomeChip` publishes — so the workforce
// console and this workbench are checkably drawing the same outcome for the same stored status.
// `live-status-chip` is the shared recolour transition, which styles/liveStates.css removes under
// prefers-reduced-motion.
function StatusChip({ status }) {
  const outcome = workOutcomeForRunStatus(status);
  const cls = workOutcomeMeta(outcome).cls;
  const label = STATUS_LABELS[status] ?? (status ? `מצב לא מוכר: ${status}` : "מצב לא ידוע");
  return (
    <span
      data-work-outcome={outcome ?? "unknown"}
      data-run-status={status ?? ""}
      className={`live-status-chip inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}
    >
      {label}
    </span>
  );
}

function SectionCard({ title, hint, children, className = "" }) {
  return (
    <section className={`rounded-xl border border-gray-200 bg-white p-3 xl:p-4 space-y-2 ${className}`}>
      <div>
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        {hint && <p className="text-xs text-gray-400">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

// The pilot default builds the deterministic mock when no ANALYSIS_PROVIDER is configured, and the
// mock marks its own output `degraded` — that prose is the fallback adapter's scaffolding, not a
// finding about this ticket. The label comes from the shared vocabulary owner every other surface
// imports, so no surface can word one arm two ways.
//
// It is drawn at the head of each block that renders analysis fields, ahead of the words it
// qualifies: a disclosure read after the prose has already been read as part of the claim. Both
// call sites render this one component, so the two cannot disagree about the same run.
function AnalysisProvenanceNote({ analysis, testId }) {
  const label = degradedAnalysisLabel(analysis?.degraded);
  if (!label) return null;
  return (
    <p
      data-testid={testId}
      className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
    >
      {label}
    </p>
  );
}

// info comes from employeeOf(); name may be unknown (deleted employee or a failed agents load),
// which is stated plainly instead of inventing an identity.
function EmployeeIdentity({ info, size = "md" }) {
  if (!info) return null;
  const displayName = info.name || "עובד דיגיטלי";
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      <EmployeeAvatar
        name={displayName}
        avatarKey={info.avatarKey}
        avatarColor={info.avatarColor}
        status={info.status}
        isEnabled={info.isEnabled}
        size={size}
      />
      <span className="text-sm font-medium text-gray-800 truncate">{displayName}</span>
      {info.roleTitle && <span className="text-xs text-gray-500 truncate">· {info.roleTitle}</span>}
      {!info.name && <span className="text-xs text-gray-400">{info.loading ? "(טוען פרטים…)" : "(שם לא זמין)"}</span>}
    </span>
  );
}

function ReviewRunCard({ run, employee, ticketId, canApprove }) {
  const qc = useQueryClient();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["agentRuns", ticketId] });
    qc.invalidateQueries({ queryKey: ["run-approval-tokens", run.id] });
  };
  const approveMutation = useMutation({ mutationFn: () => agentRunsApi.approve(run.id), onSuccess: invalidate });
  const rejectMutation = useMutation({
    mutationFn: () => agentRunsApi.reject(run.id, reason.trim() || undefined),
    onSuccess: () => {
      setRejectOpen(false);
      setReason("");
      invalidate();
    },
  });

  const { data: tokens, isError: tokensFailed } = useQuery({
    queryKey: ["run-approval-tokens", run.id],
    queryFn: () => agentRunsApi.approvalTokens(run.id),
    staleTime: 15_000,
  });

  const analysis = run.analysis || {};
  const proposed = analysis.proposed_action || null;
  const proposedLabel = proposed?.action_type
    ? actionTypeLabel(proposed.action_type)
    : null;
  const error = approveMutation.error || rejectMutation.error;
  const busy = approveMutation.isPending || rejectMutation.isPending;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2" data-testid={`workbench-review-${run.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <EmployeeIdentity info={employee} size="sm" />
          <StatusChip status={run.status} />
        </div>
        <span className="text-xs text-gray-500">
          {fmtDateTime(run.created_at) && `נוצרה ${fmtDateTime(run.created_at)}`}
          {timeAgo(run.updated_at || run.created_at) && ` · עודכנה ${timeAgo(run.updated_at || run.created_at)}`}
        </span>
      </div>

      <div className="text-sm text-gray-700 space-y-0.5">
        <AnalysisProvenanceNote analysis={analysis} testId={`workbench-analysis-degraded-${run.id}`} />
        {proposedLabel && (
          <p>
            פעולה מוצעת: <strong>{proposedLabel}</strong>
            {typeof proposed.target === "string" && proposed.target.trim() && ` — ${truncate(proposed.target, 80)}`}
          </p>
        )}
        {analysis.likely_issue && <p className="line-clamp-2">ממצא: {analysis.likely_issue}</p>}
        {!proposedLabel && !analysis.likely_issue && (
          <p className="text-gray-500">אין פירוט מוצע להצגה — ניתן לצפות בריצה המלאה.</p>
        )}
        <p className="text-xs text-gray-500">
          {analysis.risk_level != null
            ? `סיכון: ${RISK_LABELS[analysis.risk_level] ?? analysis.risk_level}`
            : "רמת סיכון לא צוינה"}
          {analysis.confidence != null && ` · ביטחון: ${analysis.confidence}%`}
        </p>
      </div>

      {tokensFailed ? (
        <p className="text-xs text-gray-500">לא ניתן לטעון את נתוני האישור (טוקנים) לריצה זו.</p>
      ) : (tokens || []).length > 0 ? (
        <ul className="text-xs text-gray-600 space-y-0.5 border-t border-amber-100 pt-1.5">
          {tokens.map((token) => (
            <li key={token.id}>
              {"אישור "}
              {actionTypeLabel(token.action_type)}
              {token.risk_level != null ? ` · סיכון ${riskGradeLabel(token.risk_level)}` : ""}
              {" · "}
              {token.revoked
                ? `בוטל${token.revoke_reason ? ` (${truncate(token.revoke_reason, 80)})` : ""}`
                : token.used
                  ? "נוצל"
                  : tokenExpired(token)
                    ? "פג תוקף"
                    : "בתוקף"}
              {token.approved_at ? ` · אושר ${fmtDateTime(token.approved_at)}` : ""}
              {token.expires_at ? ` · בתוקף עד ${fmtDateTime(token.expires_at)}` : ""}
            </li>
          ))}
        </ul>
      ) : null}

      {canApprove && !rejectOpen && (
        <div className="flex flex-wrap gap-2 pt-1 border-t border-amber-100">
          <button
            type="button"
            data-testid={`workbench-approve-${run.id}`}
            onClick={() => approveMutation.mutate()}
            disabled={busy}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
          >
            {approveMutation.isPending ? "מאשר..." : "אשר"}
          </button>
          <button
            type="button"
            data-testid={`workbench-reject-${run.id}`}
            onClick={() => setRejectOpen(true)}
            disabled={busy}
            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
          >
            דחה
          </button>
          <Link
            to={`/agent-runs/${run.id}`}
            className="border border-gray-300 hover:bg-gray-50 text-gray-600 px-3 py-1.5 rounded text-xs font-medium"
          >
            צפה בריצה המלאה
          </Link>
        </div>
      )}

      {!canApprove && (
        <p className="text-xs text-gray-500 border-t border-amber-100 pt-1.5">
          אישור או דחייה של הפעולה דורשים הרשאת אישור פעולות AI.
        </p>
      )}

      {canApprove && rejectOpen && (
        <div className="border-t border-amber-100 pt-2 space-y-2">
          <label className="block text-xs font-medium text-gray-600" htmlFor={`workbench-reject-reason-${run.id}`}>
            סיבת דחייה (אופציונלי)
          </label>
          <textarea
            id={`workbench-reject-reason-${run.id}`}
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <div className="flex flex-wrap gap-1.5">
            {REJECT_QUICK_REASONS.map((quick) => (
              <button
                key={quick}
                type="button"
                onClick={() => setReason(quick)}
                className="text-xs border border-gray-300 bg-white rounded-full px-2 py-0.5 hover:bg-gray-50"
              >
                {quick}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid={`workbench-reject-submit-${run.id}`}
              onClick={() => rejectMutation.mutate()}
              disabled={busy}
              className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
            >
              {rejectMutation.isPending ? "דוחה..." : "שלח דחייה"}
            </button>
            <button
              type="button"
              onClick={() => {
                setRejectOpen(false);
                setReason("");
              }}
              disabled={busy}
              className="border border-gray-300 hover:bg-gray-50 text-gray-600 px-3 py-1.5 rounded text-xs font-medium disabled:opacity-50"
            >
              ביטול
            </button>
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="text-xs text-red-600">
          {errorDetail(error, "שגיאה בעדכון הריצה")}
        </p>
      )}
    </div>
  );
}

function EvidenceBlock({ run }) {
  if (!run) return <p className="text-sm text-gray-500">אין ריצת AI להצגת ראיות.</p>;
  const analysis = run.analysis || null;
  const actions = Array.isArray(run.actions) ? run.actions : [];
  return (
    <div className="space-y-2 text-sm text-gray-700">
      {run.result_summary && (
        <p>
          <span className="text-xs font-medium text-gray-500 block">סיכום הריצה</span>
          {truncate(run.result_summary, 240)}
        </p>
      )}

      <div>
        <span className="text-xs font-medium text-gray-500 block">אבחון</span>
        <AnalysisProvenanceNote analysis={analysis} testId="workbench-evidence-degraded" />
        {analysis ? (
          <ul className="space-y-0.5">
            {analysis.likely_issue && <li>ממצא מרכזי: {analysis.likely_issue}</li>}
            {(analysis.confidence != null || analysis.risk_level != null) && (
              <li className="text-xs text-gray-500">
                {analysis.confidence != null && `ביטחון: ${analysis.confidence}%`}
                {analysis.confidence != null && analysis.risk_level != null && " · "}
                {analysis.risk_level != null && `סיכון: ${RISK_LABELS[analysis.risk_level] ?? analysis.risk_level}`}
              </li>
            )}
            {analysis.recommended_next_step && <li>המלצה: {analysis.recommended_next_step}</li>}
            {Array.isArray(analysis.missing_information) && analysis.missing_information.length > 0 && (
              <li className="text-amber-600">מידע חסר: {analysis.missing_information.join(" · ")}</li>
            )}
            {!analysis.likely_issue && !analysis.recommended_next_step && (
              <li className="text-gray-500">אין נתוני אבחון להצגה בריצה זו.</li>
            )}
          </ul>
        ) : (
          <p className="text-gray-500">אין נתוני אבחון להצגה בריצה זו.</p>
        )}
      </div>

      <div>
        <span className="text-xs font-medium text-gray-500 block">פעולות שבוצעו</span>
        {actions.length > 0 ? (
          <ul className="space-y-1">
            {actions.map((action) => (
              <li key={action.id} className="flex items-start justify-between gap-2">
                <span className="min-w-0">
                  <span className="block truncate">{truncate(action.description, 120)}</span>
                  <span className="text-xs text-gray-400">
                    {action.executed_at ? `בוצע ${fmtDateTime(action.executed_at)}` : "לא תועד מועד ביצוע"}
                    {action.risk_level ? ` · סיכון ${RISK_LABELS[action.risk_level] ?? action.risk_level}` : ""}
                  </span>
                </span>
                <span className={`text-xs font-medium shrink-0 ${OUTCOME_COLORS[action.outcome] || "text-gray-500"}`}>
                  {OUTCOME_LABELS[action.outcome] ?? action.outcome}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">לא תועדו פעולות בריצה זו.</p>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───

export default function TicketEmployeeWorkbench({
  ticket,
  runs = [],
  runsFailed = false,
  runsLoading = false,
  runsUpdatedAt = 0,
  onOpenConversation,
  onOpenDiagnosis,
  onStartAnalysis,
  isStartingAnalysis = false,
}) {
  const { can: canAgent } = useAgentPermissions();
  const qc = useQueryClient();
  const approvalSectionRef = useRef(null);
  const canView = canAgent("agents_view");
  const canRun = canAgent("agents_run");
  const canApprove = canAgent("agents_approve");
  const ticketId = ticket?.id ?? null;

  const { data: agents = [], isPending: agentsLoading, isError: agentsFailed } = useQuery({
    queryKey: ["agents"],
    queryFn: () => agentsApi.list(),
    staleTime: 60_000,
  });
  const { data: conversations = [], isPending: conversationsLoading, isError: conversationsFailed } = useQuery({
    queryKey: ["ticket-conversations", ticketId],
    queryFn: () => conversationsApi.list({ ticket_id: ticketId, limit: 10 }),
    enabled: !!ticketId && canView,
    staleTime: 15_000,
  });

  const sortedRuns = useMemo(
    () => [...runs].sort((a, b) => timeValue(b.created_at) - timeValue(a.created_at) || String(b.id).localeCompare(String(a.id))),
    [runs]
  );
  const latestRun = sortedRuns[0] ?? null;
  const reviewRuns = sortedRuns.filter((run) => REVIEW_STATUSES.has(run.status));

  const sortedConversations = useMemo(
    () => [...conversations].sort((a, b) => timeValue(b.updated_at, b.created_at) - timeValue(a.updated_at, a.created_at)),
    [conversations]
  );
  const latestConversation = sortedConversations[0] ?? null;

  const { data: messages = [], isPending: messagesLoading, isLoading: messagesFetching, isError: messagesFailed } = useQuery({
    queryKey: ["ticket-conversation-messages", latestConversation?.id],
    queryFn: () => conversationsApi.listMessages(latestConversation.id),
    enabled: !!latestConversation?.id,
    staleTime: 10_000,
  });

  const agentById = useMemo(() => new Map((agents || []).map((agent) => [agent.id, agent])), [agents]);
  const participantAgents = useMemo(() => {
    const map = new Map();
    for (const conversation of sortedConversations) {
      for (const participant of conversation.participants || []) {
        if (participant.role === "agent" && participant.agent_id) map.set(participant.agent_id, participant);
      }
    }
    return map;
  }, [sortedConversations]);

  const employeeOf = (agentId) => {
    if (!agentId) return null;
    const agent = agentById.get(agentId);
    if (agent) {
      return {
        id: agentId,
        name: agent.name,
        roleTitle: agent.role_title,
        avatarKey: agent.avatar_key,
        avatarColor: agent.avatar_color,
        status: agent.availability_status,
        isEnabled: agent.is_enabled,
      };
    }
    const participant = participantAgents.get(agentId);
    if (participant?.agent_name) {
      return {
        id: agentId,
        name: participant.agent_name,
        roleTitle: participant.agent_role_title,
        avatarKey: participant.agent_avatar_key,
        avatarColor: participant.agent_avatar_color,
        status: participant.agent_availability_status,
        isEnabled: participant.agent_is_enabled,
      };
    }
    return { id: agentId, name: null, loading: agentsLoading };
  };

  const engagedAgentIds = useMemo(() => {
    const ids = [];
    const push = (id) => {
      if (id && !ids.includes(id)) ids.push(id);
    };
    sortedRuns.forEach((run) => push(run.agent_id));
    sortedConversations.forEach((conversation) =>
      (conversation.participants || []).forEach((participant) => {
        if (participant.role === "agent") push(participant.agent_id);
      })
    );
    return ids;
  }, [sortedRuns, sortedConversations]);

  const participantById = useMemo(
    () => new Map((latestConversation?.participants ?? []).map((participant) => [participant.id, participant])),
    [latestConversation]
  );
  const visibleMessages = (messages || []).filter(
    (message) => message.content && ["operator_message", "human_response", "system", "handoff_notice"].includes(message.message_type)
  );
  const lastMessage = visibleMessages.length ? visibleMessages[visibleMessages.length - 1] : null;
  const lastMessageAuthor = (() => {
    if (!lastMessage) return null;
    if (lastMessage.message_type === "handoff_notice") return "הודעת ניתוב";
    if (lastMessage.message_type === "operator_message") return "הטכנאי";
    const participant = participantById.get(lastMessage.author_participant_id);
    if (participant?.role === "agent") return participant.agent_name || "עובד דיגיטלי";
    if (participant?.role === "manager") return "מנהל הצוות";
    return "המערכת";
  })();

  // ── Next action: the single clearest operator step for the CURRENT run state ──
  const nextAction = (() => {
    if (runsFailed) {
      // A failed REFRESH that still holds the last payload is stale, not empty: the operator can
      // read the last-known state, but must not be told the next action is unknowable and must not
      // act on it before a successful refresh.
      if (runs.length > 0) {
        return {
          tone: "wait",
          text: "רענון נתוני הריצות נכשל — הפעולה הבאה מוצגת מהטעינה האחרונה ואינה נוכחית; יש לרענן לפני פעולה.",
          cta: { label: "נסה שוב", kind: "retry" },
        };
      }
      return {
        tone: "error",
        text: "לא ניתן לקבוע את הפעולה הבאה — טעינת נתוני ריצות ה-AI נכשלה.",
        cta: { label: "נסה שוב", kind: "retry" },
      };
    }
    if (runsLoading) {
      return { tone: "idle", text: "טוען את מצב העבודה של הקריאה...", cta: null };
    }
    const reviewRun = reviewRuns[0];
    if (reviewRun) {
      if (canApprove) {
        return {
          tone: "approval",
          text: "עובד דיגיטלי ממתין לאישור או דחייה של פעולה מוצעת לפני ביצוע.",
          cta: { label: "אשר את הפעולה המוצעת", kind: "approve", runId: reviewRun.id },
        };
      }
      return {
        tone: "approval",
        text: "פעולה מוצעת ממתינה לאישור — נדרשת הרשאת אישור פעולות AI.",
        cta: null,
      };
    }
    if (!latestRun) {
      return canRun
        ? { tone: "idle", text: "טרם הופעל ניתוח AI לקריאה זו.", cta: { label: "הפעל ניתוח AI", kind: "start" } }
        : { tone: "idle", text: "טרם הופעל ניתוח AI לקריאה זו — נדרשת הרשאת הפעלה.", cta: null };
    }
    const status = latestRun.status;
    if (ACTIVE_STATUSES.has(status)) {
      return {
        tone: "active",
        text: `העובד הדיגיטלי פועל כעת (${STATUS_LABELS[status] ?? status}).`,
        cta: { label: "עקוב באבחון AI", kind: "diagnostics" },
      };
    }
    if (status === "waiting_for_customer_connection") {
      return { tone: "wait", text: "ממתין לחיבור מרחוק ללקוח להמשך האבחון.", cta: { label: "פתח אבחון AI", kind: "diagnostics" } };
    }
    if (status === "waiting_for_customer_answer") {
      return { tone: "wait", text: "ממתין לתשובת הלקוח.", cta: { label: "פתח אבחון AI", kind: "diagnostics" } };
    }
    if (status === "paused") {
      return { tone: "wait", text: "הריצה מושהית.", cta: { label: "פתח אבחון AI להמשך הטיפול", kind: "diagnostics" } };
    }
    if (status === "failed" || status === "rejected") {
      return canRun
        ? {
            tone: "error",
            text: status === "failed" ? "הריצה האחרונה נכשלה." : "הריצה האחרונה נדחתה.",
            cta: { label: "הרץ ניתוח חדש", kind: "start" },
          }
        : {
            tone: "error",
            text: `${status === "failed" ? "הריצה האחרונה נכשלה" : "הריצה האחרונה נדחתה"} — נדרשת הרשאת הפעלה להרצה חוזרת.`,
            cta: null,
          };
    }
    if (status === "approved") {
      return {
        tone: "active",
        text: "אושר — מבצע/ת עכשיו.",
        cta: { label: "פתח אבחון AI", kind: "diagnostics" },
      };
    }
    if (DONE_STATUSES.has(status)) {
      // A finished run with open questions is "awaiting information", not a clean completion —
      // the missing items are the operator's next material (they are listed in the evidence below).
      const missingInfo = Array.isArray(latestRun.analysis?.missing_information)
        ? latestRun.analysis.missing_information.filter(Boolean)
        : [];
      if (missingInfo.length > 0) {
        return {
          tone: "wait",
          text: 'הריצה האחרונה הושלמה, אך חסר מידע להמשך הטיפול — ראו "מידע חסר" בראיות.',
          cta: { label: "פתח אבחון AI", kind: "diagnostics" },
        };
      }
      return {
        tone: "done",
        text: "הריצה האחרונה הושלמה — סקור את הראיות והתוצאות שלמטה.",
        cta: { label: "פתח אבחון AI", kind: "diagnostics" },
      };
    }
    // An unrecognized status is never guessed into a state (in particular never reported as
    // completed) — it is labeled as unrecognized with the raw code kept as diagnostic detail.
    return {
      tone: "idle",
      text: `מצב הריצה האחרונה אינו מזוהה (${status ? `מצב לא מוכר: ${status}` : "מצב לא ידוע"}) — יש לבדוק את הריצה לפני פעולה נוספת.`,
      cta: { label: "פתח אבחון AI", kind: "diagnostics" },
    };
  })();

  // Approval failures are remembered with the ticket they happened on, so navigating to another
  // ticket never shows the previous ticket's error (this component instance survives param change).
  const [bannerError, setBannerError] = useState(null);
  const bannerApprove = useMutation({
    mutationFn: (runId) => agentRunsApi.approve(runId),
    onSuccess: () => {
      setBannerError(null);
      qc.invalidateQueries({ queryKey: ["agentRuns", ticketId] });
    },
    onError: (error) => setBannerError({ ticketId, message: errorDetail(error, "שגיאה באישור הפעולה") }),
  });
  const visibleBannerError = bannerError && bannerError.ticketId === ticketId ? bannerError.message : null;

  const handleBannerCta = () => {
    const cta = nextAction.cta;
    if (!cta) return;
    if (cta.kind === "approve") bannerApprove.mutate(cta.runId);
    else if (cta.kind === "start") onStartAnalysis?.();
    else if (cta.kind === "diagnostics") onOpenDiagnosis?.();
    else if (cta.kind === "retry") {
      setBannerError(null);
      qc.invalidateQueries({ queryKey: ["agentRuns", ticketId] });
    }
  };

  const refreshAll = () => {
    setBannerError(null);
    qc.invalidateQueries({ queryKey: ["agentRuns", ticketId] });
    qc.invalidateQueries({ queryKey: ["ticket-conversations", ticketId] });
    qc.invalidateQueries({ queryKey: ["ticket-conversation-messages", latestConversation?.id] });
  };

  // The workflow's approval step is a focus move, never an approval: the human decision stays on
  // the review card below, behind the same `agents_approve` gate and the server-side check.
  const focusApproval = (runId) => {
    const scope = approvalSectionRef.current;
    if (!scope) return;
    const target =
      (runId && scope.querySelector(`[data-testid="workbench-approve-${runId}"]`)) ||
      scope.querySelector('[data-testid^="workbench-approve-"]');
    if (!target || typeof target.focus !== "function") return;
    target.focus();
    if (typeof target.scrollIntoView === "function") target.scrollIntoView({ block: "center" });
  };

  if (!ticket || !canView) return null;

  const conversationState = latestConversation ? conversationStateOf(latestConversation.status) : null;
  const sourcesBusy = runsLoading || agentsLoading || conversationsLoading || messagesFetching;

  const primaryLabel =
    nextAction.cta?.kind === "approve"
      ? bannerApprove.isPending
        ? "מאשר..."
        : nextAction.cta.label
      : nextAction.cta?.kind === "start" && isStartingAnalysis
        ? "מפעיל..."
        : nextAction.cta?.label;

  const technicianName = ticket.technician
    ? `${ticket.technician.first_name ?? ""} ${ticket.technician.last_name ?? ""}`.trim() || null
    : null;

  return (
    <div dir="rtl" data-testid="ticket-employee-workbench" aria-busy={sourcesBusy} className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-base font-bold text-gray-800">סביבת עבודה — עובד דיגיטלי בקריאה זו</h2>
        <button
          type="button"
          data-testid="workbench-refresh"
          onClick={refreshAll}
          className="text-xs border border-gray-300 rounded-lg px-2.5 py-1 hover:bg-gray-50 text-gray-600"
        >
          רענן
        </button>
      </div>

      {/* The four artifact kinds, kept distinct and in order. This rail explains; the existing
          next-action banner, review card and evidence block below remain the controls. */}
      <TicketEvidenceWorkflow
        ticket={ticket}
        technicianName={technicianName}
        runs={sortedRuns}
        runsLoading={runsLoading}
        runsFailed={runsFailed}
        runsUpdatedAt={runsUpdatedAt}
        canRun={canRun}
        canApprove={canApprove}
        onStartAnalysis={onStartAnalysis}
        onRetry={refreshAll}
        onFocusApproval={focusApproval}
      />

      {/* Next action — the one thing the operator should do now */}
      <div className={`rounded-xl border p-3 xl:p-4 ${NEXT_ACTION_TONES[nextAction.tone] || NEXT_ACTION_TONES.idle}`} data-testid="workbench-next-action">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-gray-500 mb-0.5">הפעולה הבאה</p>
            <p className="text-sm font-medium text-gray-800">{nextAction.text}</p>
          </div>
          {nextAction.cta && (
            <button
              type="button"
              data-testid="workbench-primary-action"
              onClick={handleBannerCta}
              disabled={bannerApprove.isPending || (nextAction.cta.kind === "start" && isStartingAnalysis)}
              className="bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
            >
              {primaryLabel}
            </button>
          )}
        </div>
        {visibleBannerError && (
          <p role="alert" className="text-xs text-red-600 mt-1.5">
            {visibleBannerError}
          </p>
        )}
      </div>

      {/* Approvals — everything currently waiting on a human. The workflow's approval step
          focuses this region; it never clicks the approve button on the operator's behalf. */}
      <div ref={approvalSectionRef} id="workbench-approvals">
      <SectionCard
        title="אישורים ממתינים"
        hint="פעולות שהעובד הדיגיטלי הציע וממתינות להחלטת טכנאי"
      >
        {runsFailed ? (
          <p className="text-sm text-gray-500">לא ניתן לדעת אם יש פעולות הממתינות לאישור — נתוני הריצות לא נטענו.</p>
        ) : reviewRuns.length > 0 ? (
          <div className="space-y-2">
            {reviewRuns.map((run) => (
              <ReviewRunCard
                key={run.id}
                run={run}
                employee={employeeOf(run.agent_id)}
                ticketId={ticketId}
                canApprove={canApprove}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">אין פעולות הממתינות לאישור.</p>
        )}
      </SectionCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {/* Assignment */}
        <SectionCard title="שיוך" hint="מי מטפל בקריאה — טכנאי אנושי ועובדים דיגיטליים שפעלו בה">
          <div className="space-y-2 text-sm">
            <p>
              <span className="text-xs font-medium text-gray-500 block">טכנאי אחראי</span>
              {technicianName || "לא משויכת לטכנאי"}
            </p>
            {!technicianName && (
              <p className="text-xs text-gray-500">ניתן לשייך טכנאי מכרטיס הקריאה בראש העמוד.</p>
            )}
            <div>
              <span className="text-xs font-medium text-gray-500 block mb-1">עובדים דיגיטליים שפעלו בקריאה</span>
              {engagedAgentIds.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {engagedAgentIds.map((agentId) => (
                    <EmployeeIdentity key={agentId} info={employeeOf(agentId)} size="sm" />
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">לא נמצא עובד דיגיטלי שפעל בקריאה זו.</p>
              )}
            </div>
            {agentsFailed && (
              <p className="text-xs text-gray-500">לא ניתן לטעון את רשימת העובדים — שמות העובדים עשויים להיות חסרים.</p>
            )}
          </div>
        </SectionCard>

        {/* Progress */}
        <SectionCard title="התקדמות" hint="מצב הריצה הנוכחית והיסטוריית הריצות">
          {runsFailed ? (
            <p className="text-sm text-gray-500">לא ניתן להציג את התקדמות הריצות — טעינת הנתונים נכשלה.</p>
          ) : runsLoading ? (
            <p className="text-sm text-gray-500">טוען נתוני ריצות...</p>
          ) : latestRun ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusChip status={latestRun.status} />
                <EmployeeIdentity info={employeeOf(latestRun.agent_id)} size="sm" />
              </div>
              <p className="text-xs text-gray-500">
                {fmtDateTime(latestRun.created_at) && `נוצרה ${fmtDateTime(latestRun.created_at)}`}
                {timeAgo(latestRun.updated_at || latestRun.created_at) &&
                  ` · עודכנה ${timeAgo(latestRun.updated_at || latestRun.created_at)}`}
              </p>
              <Link to={`/agent-runs/${latestRun.id}`} className="text-xs text-blue-600 hover:underline inline-block">
                צפה בריצה המלאה
              </Link>

              {sortedRuns.length > 1 && (
                <div className="border-t border-gray-100 pt-2 space-y-1">
                  <p className="text-xs font-medium text-gray-500">ריצות קודמות</p>
                  {sortedRuns.slice(1, 5).map((run) => (
                    <div key={run.id} className="flex items-center justify-between gap-2 text-xs">
                      <span className="flex items-center gap-1.5 min-w-0">
                        <StatusChip status={run.status} />
                        <span className="text-gray-600 truncate">{employeeOf(run.agent_id)?.name || "עובד דיגיטלי"}</span>
                      </span>
                      <span className="text-gray-400 shrink-0">{timeAgo(run.created_at)}</span>
                    </div>
                  ))}
                  {sortedRuns.length > 5 && (
                    <p className="text-xs text-gray-400">ועוד {sortedRuns.length - 5} ריצות</p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">טרם בוצעה ריצת AI לקריאה זו.</p>
          )}
        </SectionCard>

        {/* Evidence */}
        <SectionCard title="ראיות ותוצאות" hint={latestRun ? "מתוך הריצה האחרונה" : undefined}>
          {runsFailed ? (
            <p className="text-sm text-gray-500">לא ניתן להציג ראיות — טעינת נתוני הריצות נכשלה.</p>
          ) : runsLoading ? (
            <p className="text-sm text-gray-500">טוען נתוני ריצות...</p>
          ) : (
            <EvidenceBlock run={latestRun} />
          )}
        </SectionCard>

        {/* Conversation */}
        <SectionCard title="שיחה עם עובד דיגיטלי" hint="השיחה האחרונה שנקשרה לקריאה זו">
          {conversationsFailed ? (
            <p className="text-sm text-gray-500">לא ניתן לטעון את השיחות של קריאה זו.</p>
          ) : conversationsLoading ? (
            <p className="text-sm text-gray-500">טוען שיחות...</p>
          ) : latestConversation ? (
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500">
                <span className={`px-2 py-0.5 rounded-full font-semibold border ${conversationState.badge}`}>
                  {conversationState.label}
                </span>
                <span>עודכנה {timeAgo(latestConversation.updated_at || latestConversation.created_at) || "במועד לא ידוע"}</span>
              </div>
              {messagesFailed ? (
                <p className="text-gray-500">לא ניתן לטעון את תוכן השיחה.</p>
              ) : messagesLoading ? (
                <p className="text-gray-500">טוען תוכן שיחה...</p>
              ) : lastMessage ? (
                <blockquote className="border-r-2 border-violet-200 pr-2 text-gray-700">
                  <span className="block">“{truncate(lastMessage.content, 200)}”</span>
                  <span className="text-xs text-gray-400">
                    {lastMessageAuthor}
                    {timeAgo(lastMessage.created_at) && ` · ${timeAgo(lastMessage.created_at)}`}
                  </span>
                </blockquote>
              ) : (
                <p className="text-gray-500">אין הודעות טקסט להצגה בשיחה זו.</p>
              )}
              {sortedConversations.length > 1 && (
                <p className="text-xs text-gray-400">ועוד {sortedConversations.length - 1} שיחות בקריאה זו</p>
              )}
              <button
                type="button"
                data-testid="workbench-open-conversation"
                onClick={onOpenConversation}
                className="border border-violet-300 text-violet-700 hover:bg-violet-50 px-3 py-1.5 rounded-lg text-xs font-medium"
              >
                פתח שיחה בטאב שיחה עם AI
              </button>
            </div>
          ) : (
            <p className="text-sm text-gray-500">לא התקיימה שיחה עם עובד דיגיטלי בקריאה זו.</p>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
